import React from 'react';

import {PartJSON} from 'biojs/Part';
import {SchemaTag} from 'folder-items/FileConstants';
import EntryLinkInput from 'metadata/EntryLinkInput';
import LongTextValueEditor from 'metadata/LongTextValueEditor';
import SelectorOptionInput from 'metadata/SelectorOptionInput';
import TagValueViewer from 'metadata/TagValueViewer';
import TextAndUrlValueEditor from 'metadata/TextAndUrlValueEditor';
import StorableLinkInput from 'samples/StorableLinkInput';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import * as SchemaFieldUtil from 'schemas/fields/SchemaFieldUtil';
import BioentityLinkInput from 'schemas/tags/BioentityLinkInput';
import BlobLinkInput from 'schemas/tags/BlobLinkInput';
import BooleanValueEditor from 'schemas/tags/BooleanValueEditor';
import DatetimeValueEditor from 'schemas/tags/DatetimeValueEditor';
import DateValueEditor from 'schemas/tags/DateValueEditor';
import FloatValueEditor from 'schemas/tags/FloatValueEditor';
import IntegerValueEditor from 'schemas/tags/IntegerValueEditor';
import {assertNoLegalTextSelector} from 'schemas/tags/LegalTextSelectors';
import RequiredFieldPathAwareBioentityLinkInput from 'schemas/tags/RequiredFieldPathAwareBioentityLinkInput';
import * as TagUtil from 'schemas/tags/TagUtil';
import {ChipSize} from 'ui-library/chips/Chip';
import {OptionData} from 'ui-library/menus/MenuConstants';
import UnitAwareTagValueEditor from 'units/UnitAwareTagValueEditor';
import * as logging from 'util/Logging';
import {ElementKeyboardEvent} from 'util/Types';

export interface TagValueEditorProps {
  autoFocus: boolean;
  fileId?: string;
  itemLinkAllowed?: boolean;
  noResultsOptions?: Array<{option: OptionData; onClick: () => void}> | null;
  // onChange is called with a partial tag containing only the changed fields. The caller should then merge
  // this into the old tag to get the updated tag.
  onChange: (value: object) => void;
  onKeyDown: (e: ElementKeyboardEvent) => void;
  schemaFieldSelectorId?: string | null;
  sequenceParts?: Array<PartJSON> | null;
  summarize: boolean;
  tag: SchemaTag;
  isDisabled?: boolean;
}

/**
 * Component for entering a field editor based on a particular field type.
 *
 * Renders a read-only TagValueViewer for computed tags
 */
export default function TagValueEditor(props: TagValueEditorProps): JSX.Element {
  const placeholder = TagUtil.getTagPlaceholder(props.tag);

  if (props.tag && props.tag.tagSchemaField) {
    if (SchemaFieldUtil.isUneditableField(props.tag.tagSchemaField)) {
      return <TagValueViewer tag={props.tag} />;
    }
    assertNoLegalTextSelector(props.tag.tagSchemaField);
    const fieldType = TagUtil.getFullFieldType(props.tag.tagSchemaField.fieldType);
    switch (fieldType) {
      case FIELD_TYPES.TEXT:
        break;
      case FIELD_TYPES.LONG_TEXT:
        return (
          <LongTextValueEditor
            defaultValue={props.tag}
            autoFocus={props.autoFocus}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            placeholder={placeholder ?? 'Value'}
            isDisabled={props.isDisabled}
          />
        );
      case FIELD_TYPES.INTEGER: {
        const editor = (
          <IntegerValueEditor
            defaultValue={props.tag}
            autoFocus={props.autoFocus}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            placeholder={placeholder}
            isDisabled={props.isDisabled}
          />
        );
        if (props.tag.tagSchemaField.unitSymbol != null) {
          return (
            <UnitAwareTagValueEditor
              editorComponent={editor}
              unitSymbol={props.tag.tagSchemaField.unitSymbol}
            />
          );
        }
        return editor;
      }
      case FIELD_TYPES.FLOAT: {
        const editor = (
          <FloatValueEditor
            defaultValue={props.tag}
            autoFocus={props.autoFocus}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            placeholder={placeholder}
            isDisabled={props.isDisabled}
          />
        );
        if (props.tag.tagSchemaField.unitSymbol != null) {
          return (
            <UnitAwareTagValueEditor
              editorComponent={editor}
              unitSymbol={props.tag.tagSchemaField.unitSymbol}
            />
          );
        }
        return editor;
      }
      case FIELD_TYPES.BOOLEAN:
        return (
          <BooleanValueEditor
            defaultValue={props.tag.booleanValue}
            onUpdate={props.onChange}
            placeholder={placeholder ?? 'Value'}
            isDisabled={props.isDisabled}
          />
        );
      case FIELD_TYPES.DATE:
        return (
          <DateValueEditor
            defaultValue={props.tag}
            autoFocus={props.autoFocus}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            placeholder={placeholder}
            isDisabled={props.isDisabled}
          />
        );
      case FIELD_TYPES.DATETIME:
        return (
          <DatetimeValueEditor
            defaultValue={props.tag}
            autoFocus={props.autoFocus}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            placeholder={placeholder}
            isDisabled={props.isDisabled}
          />
        );
      case FIELD_TYPES.SELECTOR:
        return (
          <SelectorOptionInput
            defaultValue={{
              ...props.tag,
              schemaFieldSelectorOptionIds: props.tag.schemaFieldSelectorOptionIds ?? [],
            }}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            placeholder={placeholder}
            schemaFieldSelectorId={
              props.schemaFieldSelectorId || props.tag.tagSchemaField.schemaFieldSelectorId
            }
            shouldAllowMultiple={false}
            disabled={props.isDisabled}
          />
        );
      case FIELD_TYPES.BIOENTITY_LINK:
        return (
          <RequiredFieldPathAwareBioentityLinkInput
            fileId={props.fileId}
            autoFocus={props.autoFocus}
            placeholder={placeholder ?? 'Entity Link'}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            tag={props.tag}
            summarize={props.summarize}
            noResultsOptions={props.noResultsOptions}
            chipSize={ChipSize.SMALL}
            isDisabled={props.isDisabled}
          />
        );
      case FIELD_TYPES.PART_LINK:
        return (
          <BioentityLinkInput
            autoFocus={props.autoFocus}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            placeholder={placeholder ?? 'Part Link'}
            sequenceParts={props.sequenceParts}
            tag={props.tag}
            chipSize={ChipSize.SMALL}
            isDisabled={props.isDisabled}
          />
        );
      case FIELD_TYPES.TRANSLATION_LINK:
        return (
          <BioentityLinkInput
            autoFocus={props.autoFocus}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            placeholder={placeholder ?? 'Translation Link'}
            tag={props.tag}
            chipSize={ChipSize.SMALL}
            isDisabled={props.isDisabled}
          />
        );
      case FIELD_TYPES.TRANSCRIPTION_LINK:
        return (
          <BioentityLinkInput
            autoFocus={props.autoFocus}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            placeholder={placeholder ?? 'Transcription Link'}
            tag={props.tag}
            chipSize={ChipSize.SMALL}
            isDisabled={props.isDisabled}
          />
        );
      case FIELD_TYPES.AA_PART_LINK:
        return (
          <BioentityLinkInput
            autoFocus={props.autoFocus}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            placeholder={placeholder ?? 'AA Part Link'}
            tag={props.tag}
            chipSize={ChipSize.SMALL}
            isDisabled={props.isDisabled}
          />
        );
      case FIELD_TYPES.BASE_MOLECULE_LINK:
        return (
          <BioentityLinkInput
            autoFocus={props.autoFocus}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            placeholder={placeholder ?? 'Base Molecule Link'}
            tag={props.tag}
            chipSize={ChipSize.SMALL}
            isDisabled={props.isDisabled}
          />
        );
      case FIELD_TYPES.STORABLE_LINK:
        return (
          <StorableLinkInput
            autoFocus={props.autoFocus}
            defaultValue={props.tag}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            summarize={props.summarize}
            noResultsOptions={props.noResultsOptions}
            placeholder={placeholder ?? 'Inventory Link'}
            includeArchived={false}
            isDisabled={props.isDisabled}
          />
        );
      case FIELD_TYPES.ENTRY_LINK:
        return (
          <EntryLinkInput
            autoFocus={props.autoFocus}
            defaultValue={props.tag}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            placeholder={placeholder}
            isDisabled={props.isDisabled}
          />
        );
      case FIELD_TYPES.BLOB_LINK:
        return (
          <BlobLinkInput
            defaultValue={props.tag}
            onChange={props.onChange}
            placeholder={placeholder}
            isDisabled={props.isDisabled}
          />
        );
      default:
        logging.warn(`Unexpected fieldType: ${fieldType.type}`, {
          extra: {
            defaultValue: props.tag,
            fileId: props.fileId,
          },
        });
    }
  }
  // Use default editor for text schema tags and custom tags.
  return (
    <TextAndUrlValueEditor
      autoFocus={props.autoFocus}
      defaultValue={props.tag}
      fileId={props.fileId}
      itemLinkAllowed={props.itemLinkAllowed}
      onChange={props.onChange}
      onKeyDown={props.onKeyDown}
      placeholder={placeholder}
      isDisabled={props.isDisabled}
    />
  );
}

TagValueEditor.defaultProps = {
  autoFocus: false,
  itemLinkAllowed: true,
  summarize: false,
};
